import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import axiosInstance from "pages/Utility/axiosInstance";
import axios from "axios";
import { useSelector } from "react-redux";

const EditLessonModal = ({ visible, handleClose, initialData = {}, onSubmit, courseOptions }) => {
  const [modules, setModules] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const loading = useSelector((state) => state.Lesson.loading);

  //  Extract IDs
  const initialCourseId =
    initialData?.chapterId?.moduleId?.courseId?._id ||
    initialData?.chapterId?.moduleId?.courseId ||
    "";
  const initialModuleId = initialData?.chapterId?.moduleId?._id || "";
  const initialChapterId = initialData?.chapterId?._id || "";

  const [selectedCourse, setSelectedCourse] = useState(initialCourseId);
  const [selectedModule, setSelectedModule] = useState(initialModuleId);

  // ============================
  // Fetch Modules & Chapters
  // ============================
  const fetchModules = async (courseId) => {
    if (!courseId) return setModules([]);
    try {
      const res = await axiosInstance.get(`V1/modules/dropdown/${courseId}`);
      setModules(res.data?.data || []);
    } catch {
      setModules([]);
    }
  };

  const fetchChapters = async (moduleId) => {
    if (!moduleId) return setChapters([]);
    try {
      const res = await axiosInstance.get(`V1/chapters/by-module/${moduleId}`);
      setChapters(res.data?.data || []);
    } catch {
      setChapters([]);
    }
  };

  useEffect(() => {
    if (visible && initialCourseId) fetchModules(initialCourseId);
  }, [visible, initialCourseId]);

  useEffect(() => {
    if (visible && initialModuleId) fetchChapters(initialModuleId);
  }, [visible, initialModuleId]);

  useEffect(() => {
    if (selectedCourse) {
      fetchModules(selectedCourse);
      setSelectedModule(null);
      setChapters([]);
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedModule) fetchChapters(selectedModule);
  }, [selectedModule]);

  // ============================
  // Presigned Upload Helpers
  // ============================
  const getPresignedUrl = async (file) => {
    const res = await axiosInstance.post("V1/common/pre-signed-url", {
      fileName: file.name,
      fileType: file.type,
    });
    return res.data; // { uploadURL, fileKey, publicUrl }
  };

  const uploadFileToPresignedUrl = async (file, url) => {
    setUploading(true);
    await axios.put(url, file, {
      headers: { "Content-Type": file.type },
      onUploadProgress: (e) => {
        setProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    setUploading(false);
  };

  // ============================
  // Formik
  // ============================
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      courseId: initialCourseId,
      moduleId: initialModuleId,
      chapterId: initialChapterId,
      title: initialData?.title || "",
      contentType: initialData?.contentType || "video",
      contentURL: initialData?.contentURL || "",
      orderIndex: initialData?.orderIndex || 1,
      createdBy: initialData?.createdBy?._id || initialData?.createdBy || "",

    },
    validationSchema: Yup.object({
      courseId: Yup.string().required("Course is required"),
      moduleId: Yup.string().required("Module is required"),
      chapterId: Yup.string().required("Chapter is required"),
      title: Yup.string().required("Title is required"),
      orderIndex: Yup.number().required("Order is required").min(1, "Minimum 1"),
      contentType: Yup.string().required("Type is required"),
    }),
    onSubmit: async (values) => {
      if (!initialData?._id) return;

      let payload = {
        title: values.title,
        orderIndex: values.orderIndex,
        contentType: values.contentType,
        chapterId: values.chapterId,
        //  createdBy: values.createdBy || initialData?.createdBy?._id,
      };

      // For Video
      if (values.contentType === "video") {
        payload.contentURL = values.contentURL;
      }

      // For File Upload
      if (values.contentType === "file" && file) {
        try {
          const { uploadURL, publicUrl } = await getPresignedUrl(file);
          await uploadFileToPresignedUrl(file, uploadURL);
          payload.contentURL = publicUrl;
        } catch (err) {
          console.error("File upload failed", err);
          return;
        }
      }

      onSubmit(initialData._id, payload, onClose);
    },
  });

  const onClose = () => {
    formik.resetForm();
    setFile(null);
    setProgress(0);
    setUploading(false);
    handleClose();
  };

  // ============================
  // Render
  // ============================
  return (
    <Modal
      title="Edit Lesson"
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={500}
      maskClosable={false}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="row g-3">
          {/* Course */}
          <div className="col-12">
            <label>Course *</label>
            <Select
              options={courseOptions}
              value={courseOptions.find((opt) => opt.value === formik.values.courseId) || null}
              onChange={(opt) => {
                const courseId = opt?.value || "";
                formik.setFieldValue("courseId", courseId);
                formik.setFieldValue("moduleId", "");
                formik.setFieldValue("chapterId", "");
                setSelectedCourse(courseId);
              }}
              isClearable
            />
          </div>

          {/* Module */}
          <div className="col-12">
            <label>Module *</label>
            <Select
              options={modules.map((m) => ({ label: m.title, value: m._id }))}
              value={
                formik.values.moduleId
                  ? {
                      label:
                        modules.find((m) => m._id === formik.values.moduleId)?.title || "Module",
                      value: formik.values.moduleId,
                    }
                  : null
              }
              onChange={(opt) => {
                const moduleId = opt?.value || "";
                formik.setFieldValue("moduleId", moduleId);
                formik.setFieldValue("chapterId", "");
                setSelectedModule(moduleId);
              }}
              isDisabled={!formik.values.courseId}
            />
          </div>

          {/* Chapter */}
          <div className="col-12">
            <label>Chapter *</label>
            <Select
              options={chapters.map((ch) => ({ label: ch.title, value: ch._id }))}
              value={
                formik.values.chapterId
                  ? {
                      label:
                        chapters.find((ch) => ch._id === formik.values.chapterId)?.title ||
                        "Chapter",
                      value: formik.values.chapterId,
                    }
                  : null
              }
              onChange={(opt) => formik.setFieldValue("chapterId", opt?.value || "")}
              isDisabled={!formik.values.moduleId}
            />
          </div>

          {/* Title */}
          <div className="col-12">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              className="form-control"
              value={formik.values.title}
              onChange={formik.handleChange}
            />
          </div>

          {/* Content Type */}
          <div className="col-12">
            <label>Content Type *</label>
            <select
              className="form-control"
              name="contentType"
              value={formik.values.contentType}
              onChange={(e) => {
                const newType = e.target.value;
                const oldType = formik.values.contentType;
                formik.setFieldValue("contentType", newType);
                setFile(null);
                setProgress(0);
                if (newType === "file") {
                  formik.setFieldValue("contentURL", "");
                } else if (newType === "video" && oldType === "file") {
                  formik.setFieldValue("contentURL", initialData?.contentURL || "");
                }
              }}
            >
              <option value="video">Video</option>
              <option value="file">File</option>
            </select>
          </div>

          {/* Video URL */}
          {formik.values.contentType === "video" && (
            <div className="col-12">
              <label>Content URL *</label>
              <input
                type="text"
                name="contentURL"
                className="form-control"
                value={formik.values.contentURL}
                onChange={formik.handleChange}
                placeholder="Enter video URL"
              />
            </div>
          )}

          {/* File Upload */}
          {formik.values.contentType === "file" && (
            <div className="col-12">
              <label>Upload File *</label>
              {!formik.values.contentURL ? (
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="form-control"
                  onChange={async (e) => {
                    const selectedFile = e.target.files[0];
                    if (!selectedFile) return;
                    setFile(selectedFile);
                    setProgress(0);
                  }}
                />
              ) : (
                <div className="d-flex align-items-center">
                  <a href={formik.values.contentURL} target="_blank" rel="noreferrer">
                    View File
                  </a>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger ms-2"
                    onClick={() => formik.setFieldValue("contentURL", "")}
                  >
                    ×
                  </button>
                </div>
              )}
              {uploading && (
                <div className="mt-1 text-muted">Uploading: {progress}%</div>
              )}
            </div>
          )}
          {/* Created By */}
<div className="col-12">
  <label>Created By</label>
  <input
    type="text"
    className="form-control"
    value={
      initialData?.createdBy?.name ||
      initialData?.createdBy?.email ||
      "-"
    }
    disabled
  />
</div>


          {/* Order Index */}
          <div className="col-12">
            <label>Order Index *</label>
            <input
              type="number"
              name="orderIndex"
              className="form-control"
              value={formik.values.orderIndex}
              onChange={formik.handleChange}
            />
          </div>

          {/* Footer */}
          <div className="modal-footer mt-3">
            <button type="button" className="btn btn-light " onClick={onClose}>
              Close
            </button>
            <button type="submit" className="btn btn-primary ms-3" disabled={loading || uploading}>
              {loading || uploading ? <ClipLoader size={16} color="white" /> : "Update"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default EditLessonModal;
